import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import CustomerProfilePane from '../components/customers/CustomerProfilePane';
import customerService, { CustomerDetail, TimelineEntry } from '../services/customerService';

/** Mobile-only route: on desktop the profile renders inside CustomersPage. */
export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [timeline, setTimeline] = useState<TimelineEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (customerId: number) => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await customerService.getDetail(customerId);
      setCustomer(res.customer);
      setTimeline(res.timeline || []);
    } catch {
      setError('Kundenprofil konnte nicht geladen werden.');
      setCustomer(null);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const numericId = Number(id);
    if (id && !Number.isNaN(numericId)) load(numericId);
  }, [id, load]);

  return (
    <Layout>
      <div className="container mx-auto max-w-3xl space-y-6 py-8 animate-in fade-in duration-500">
        <div className="flex items-center gap-3 border-b pb-5">
          <Button variant="ghost" size="icon" onClick={() => navigate('/customers')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Kundenprofil</h1>
        </div>

        {isLoading ? (
          <div className="space-y-4">
            <Skeleton className="h-16 w-1/2 rounded-lg" />
            <Skeleton className="h-64 w-full rounded-lg" />
          </div>
        ) : error ? (
          <div className="py-16 text-center text-muted-foreground">{error}</div>
        ) : !customer ? (
          <div className="py-16 text-center text-muted-foreground">Kunde nicht gefunden</div>
        ) : (
          <div className="rounded-xl border border-border bg-card p-4">
            <CustomerProfilePane customer={customer} timeline={timeline} />
          </div>
        )}
      </div>
    </Layout>
  );
}
